import { floor, floatval, intval, trim, type PhpFloat } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
import { getVitalityDieFromCreatureSize } from '../../compile/creature-vitality';
import { vitalityStatusCalculateStatus } from './additions';
import { catalogId } from './catalog';
import { empty } from '../../php-compat';
import { slot, values, type Arr } from './access';

/** Legacy PHP symbol: get_v_die_from_creature_size. */
export { getVitalityDieFromCreatureSize as getVDieFromCreatureSize } from '../../compile/creature-vitality';

/** Legacy PHP symbol: calc_v_avg_from_v_dice. */
export function calcVAvgFromVDice(size: string, dice: number, body: number, bonus: unknown = 0): number {
    if (typeof bonus === 'string') bonus = trim(bonus) === '' ? 0 : floatval(bonus);
    return Number(intval(floor(dice * ((getVitalityDieFromCreatureSize(size) + 1) / 2) + dice * body + Number(bonus))));
}
/** Legacy PHP symbol: calc_v_max_from_v_dice. */
export function calcVMaxFromVDice(size: string, dice: number, body: number, bonus: unknown = 0): number {
    if (typeof bonus === 'string') bonus = trim(bonus) === '' ? 0 : floatval(bonus);
    return Number(intval(floor(dice * getVitalityDieFromCreatureSize(size) + dice * body + Number(bonus))));
}
export interface RandomVitalityDeps { roll_dice(dice: number, die: number, mod: number): number; }
/** Legacy PHP symbol: calc_v_random_from_v_dice. */
export function calcVRandomFromVDice(size: string, dice: number, body: number, bonus: unknown = 0, env: PortEnv<RandomVitalityDeps>): number {
    const rolled = env.deps.roll_dice(dice, getVitalityDieFromCreatureSize(size), dice * body);
    return Number(intval(floor(rolled + Number(intval(bonus)))));
}
/** Legacy PHP symbol: calc_v_die_used. */
export function calcVDieUsed(cr: number, size: string): string { return cr + 'd' + getVitalityDieFromCreatureSize(size); }
/** Legacy PHP symbol: calc_v_status. */
export function calcVStatus(current: number, maximum: number, temp = 0, _maxTemp = 0, env: PortEnv<object>): string {
    return vitalityStatusCalculateStatus(current, maximum, temp, env);
}

export interface AcDeps {
    layer_value(row: Arr, name: string): unknown;
    item_arr_numeric_slot(value: unknown, fallback: number | PhpFloat): unknown;
    item_arr_scalar_layer_slot(value: unknown): string;
    check_uuid(value: unknown): boolean;
    query_full_item_arr(id: string): Arr | null;
    ft_compute_persist(row: PhpRef<Arr>, category: string, key: string, value: unknown): void;
    resolve_inventory_layer_list_entry(value: unknown): Arr;
    ft_resolve_item_id_type(id: string, row: Arr): string;
}
/** Legacy PHP symbol: initialize_ac_values. */
export function initializeAcValues(row: PhpRef<Arr>, env: PortEnv<AcDeps>): null {
    env.deps.ft_compute_persist(row, 'ac', 'max_body', intval(env.deps.item_arr_numeric_slot(env.deps.layer_value(row.value, 'body') ?? 0, floatval(0))));
    for (const key of ['natural_armor', 'armor', 'shield', 'total_mod']) env.deps.ft_compute_persist(row, 'ac', key, 0);
    return null;
}
/** Legacy PHP symbol: process_natural_armor. */
export function processNaturalArmor(row: PhpRef<Arr>, env: PortEnv<AcDeps>): null {
    const id = env.deps.item_arr_scalar_layer_slot(env.deps.layer_value(row.value, 'natural_armor') ?? null);
    if (id === '' || !env.deps.check_uuid(id)) return null;
    const natural = env.deps.query_full_item_arr(id);
    if (empty(natural)) return null;
    env.deps.ft_compute_persist(row, 'ac', 'natural_armor', intval(env.deps.item_arr_numeric_slot(env.deps.layer_value(natural as Arr, 'ac_mod') ?? 0, floatval(0))));
    if (env.deps.layer_value(natural as Arr, 'max_body_ac_mod') !== null) {
        const maximum = intval(env.deps.item_arr_numeric_slot(env.deps.layer_value(natural as Arr, 'max_body_ac_mod') ?? 0, floatval(0)));
        if (maximum > 0) {
            const current = intval(env.deps.item_arr_numeric_slot(env.deps.layer_value(row.value, 'max_body_ac_mod') ?? 0, floatval(0)));
            env.deps.ft_compute_persist(row, 'ac', 'max_body', current > maximum || current <= 0 ? maximum : current);
        }
    }
    return null;
}
/** Legacy PHP symbol: process_equipped_items. */
export function processEquippedItems(row: PhpRef<Arr>, env: PortEnv<AcDeps>): null {
    const naturalId = env.deps.item_arr_scalar_layer_slot(env.deps.layer_value(row.value, 'natural_armor') ?? '');
    if (!empty(naturalId) && (env.deps.layer_value(row.value, 'equipped_armor_inventory_item_id') ?? '') === naturalId)
        env.deps.ft_compute_persist(row, 'ac', 'equipped_armor_id', '');
    if (!empty(slot(row.value, 'encounter_creatures', 'encounter_creature_id')) || empty(slot(row.value, 'inventory_equipped'))) return null;
    env.deps.ft_compute_persist(row, 'ac', 'equipped_armor_id', '');
    env.deps.ft_compute_persist(row, 'ac', 'equipped_shield_id', '');
    for (const item of values(slot(row.value, 'inventory_equipped') as Arr)) {
        const resolved = env.deps.resolve_inventory_layer_list_entry(item);
        const id = slot(resolved, 'item_uuid') as string;
        if (id === '' || !env.deps.check_uuid(id)) continue;
        if (env.deps.ft_resolve_item_id_type(id, row.value) === 'armor') {
            const data = env.deps.query_full_item_arr(id) as Arr;
            if (env.deps.layer_value(data, 'armor_type') !== null) {
                const key = env.deps.layer_value(data, 'armor_type') === catalogId('ArmorType', 'shield', env) ? 'equipped_shield_id' : 'equipped_armor_id';
                env.deps.ft_compute_persist(row, 'ac', key, id);
            }
        }
    }
    return null;
}
